import useUrlToken from '../utils/useUrlToken';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Loader2, Mail, RefreshCw } from 'lucide-react';
import TrustTradeLogo from '../components/TrustTradeLogo';
import { Product as D } from '../components/productTokens';

export default function VerifyEmail() {
  const token = useUrlToken();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [status, setStatus] = useState(token ? 'verifying' : 'waiting'); // verifying | success | error | waiting
  const [errorMsg, setErrorMsg] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const hasVerified = useRef(false);

  useEffect(() => {
    if (!token || hasVerified.current) return;
    hasVerified.current = true;

    api.post('/auth/verify-email', { token })
      .then(res => {
        const { session_token, user_id, email, name, is_admin } = res.data;
        login({ user_id, email, name, is_admin: is_admin || false }, session_token);
        setStatus('success');
        setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
      })
      .catch(err => {
        setStatus('error');
        setErrorMsg(err.response?.data?.detail || 'Verification failed. The link may have expired.');
      });
  }, [token, login, navigate]);

  useEffect(() => {
    if (cooldown > 0) {
      const t = setTimeout(() => setCooldown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [cooldown]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) { toast.error('Enter your email address'); return; }
    setResendLoading(true);
    try {
      await api.post('/auth/resend-verification', { email: resendEmail });
      setResendSent(true);
      setCooldown(120);
      toast.success('Verification email sent — check your inbox.');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send email';
      if (err.response?.status === 429) {
        toast.error(detail);
        setCooldown(120);
      } else {
        toast.error(detail);
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="tt-product min-h-screen flex items-center justify-center p-4" style={{ background: D.bg, backgroundImage: D.bgGlow }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-block mb-2">
            <TrustTradeLogo dark size="large" showText className="mx-auto" />
          </Link>
        </div>

        <div className="rounded-xl p-8 text-center" style={{ background: D.surfaceSolid, border: `1px solid ${D.border}`, boxShadow: D.shadow }}>

          {/* Verifying */}
          {status === 'verifying' && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `${D.accent}24` }}>
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: D.accent }} />
              </div>
              <h1 className="text-xl font-bold mb-2" style={{ color: D.text }}>Verifying your email…</h1>
              <p className="text-sm" style={{ color: D.sub }}>Just a moment.</p>
            </>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `${D.success}24` }}>
                <CheckCircle className="w-8 h-8" style={{ color: D.success }} />
              </div>
              <h1 className="text-xl font-bold mb-2" style={{ color: D.text }}>Email verified!</h1>
              <p className="text-sm" style={{ color: D.sub }}>Redirecting you to your dashboard…</p>
            </>
          )}

          {/* Error */}
          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `${D.error}24` }}>
                <XCircle className="w-8 h-8" style={{ color: D.error }} />
              </div>
              <h1 className="text-xl font-bold mb-2" style={{ color: D.text }}>Verification failed</h1>
              <p className="text-sm mb-6" style={{ color: D.sub }}>{errorMsg}</p>

              <form onSubmit={handleResend} className="text-left space-y-3">
                <p className="text-sm font-medium" style={{ color: D.text }}>Request a new verification link:</p>
                <input
                  type="email"
                  value={resendEmail}
                  onChange={e => setResendEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full rounded-md px-3 py-2 text-sm focus:outline-none"
                  style={{ background: D.hover, border: `1px solid ${D.border}`, color: D.text }}
                />
                <button
                  type="submit"
                  disabled={resendLoading || cooldown > 0}
                  className="w-full disabled:opacity-50 font-semibold text-sm py-2 px-4 rounded-md flex items-center justify-center gap-2 transition-colors"
                  style={{ background: D.accentGradient, color: '#061421' }}
                >
                  {resendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {cooldown > 0 ? `Resend in ${cooldown}s` : 'Send new verification email'}
                </button>
              </form>
            </>
          )}

          {/* Waiting (no token — came from registration) */}
          {status === 'waiting' && (
            <>
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `${D.accent}24` }}>
                <Mail className="w-8 h-8" style={{ color: D.accent }} />
              </div>
              <h1 className="text-xl font-bold mb-2" style={{ color: D.text }}>Check your email</h1>
              <p className="text-sm mb-6 leading-relaxed" style={{ color: D.sub }}>
                We sent a verification link to your email address. Click the link in the email
                to activate your account. The link expires in 24 hours.
              </p>
              <p className="text-xs mb-6" style={{ color: D.dim }}>Didn't get it? Check your spam folder or request a new link below.</p>

              {resendSent ? (
                <div className="rounded-md p-3 text-sm flex items-center gap-2" style={{ background: `${D.success}24`, border: `1px solid ${D.success}4D`, color: D.success }}>
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  Verification email sent — check your inbox.
                </div>
              ) : (
                <form onSubmit={handleResend} className="text-left space-y-3">
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={e => setResendEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="w-full rounded-md px-3 py-2 text-sm focus:outline-none"
                    style={{ background: D.hover, border: `1px solid ${D.border}`, color: D.text }}
                  />
                  <button
                    type="submit"
                    disabled={resendLoading || cooldown > 0}
                    className="w-full disabled:opacity-50 font-semibold text-sm py-2 px-4 rounded-md flex items-center justify-center gap-2 transition-colors"
                    style={{ background: D.accentGradient, color: '#061421' }}
                  >
                    {resendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend verification email'}
                  </button>
                </form>
              )}
            </>
          )}

          <div className="mt-6 pt-6" style={{ borderTop: `1px solid ${D.border}` }}>
            <Link to="/login" className="text-sm hover:underline" style={{ color: D.accent }}>
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
